import { useEffect, useMemo, useState } from 'react';
import { Key } from 'react';
import { Button, Cell, CellText, Flex, Text, TextField } from '@backstage/ui';
import { toastApiRef, ToastApi, useApi } from '@backstage/frontend-plugin-api';
import { useAsyncEntity, useEntity } from '@backstage/plugin-catalog-react';
import {
  Account,
  AccountEntity,
  AccountUser,
  AccountUserRole,
  AuthType,
  Schedule,
  UserEntity,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  DataTable,
  DataColumnConfig,
  ButtonIconBack,
  ModernCustomCard,
  ModernSelect,
} from '@internal/backstage-plugin-devops-react';
import {
  ProvidersApi,
  providersApiRef,
} from '@internal/backstage-plugin-catalog-devops-react';
import { AccountApi, accountApiRef } from '../../api';
import { useAccountContext } from '../Providers';
import { AccountScanScheduleInput } from '../Inputs';
import { buildAccountPayload, normalizeEntityRef } from './useAccountSettings';
import styles from './AccountSettingsPage.module.css';

interface MemberRow {
  key: string;
  displayName: string;
  userRef: string;
}

interface AccountSettingsPageProps {
  /** Called when the entry slide-in finished and the page covers the tab. */
  onSettled?: () => void;
  /** Called when the exit slide-out starts. */
  onUnsettled?: () => void;
  /**
   * 'overlay' (default) slides over the closest positioned ancestor and
   * settles in-flow once covered. 'fixed' keeps the overlay position inside
   * a fixed wrapper — used by the account header so settings work from
   * every account tab.
   */
  variant?: 'overlay' | 'fixed';
}

export function AccountSettingsPage(
  props: Readonly<AccountSettingsPageProps> = {},
) {
  const { onSettled, onUnsettled, variant = 'overlay' } = props;

  const { entity } = useEntity();
  const account = entity as AccountEntity;
  const { refresh } = useAsyncEntity();

  const accountApi = useApi<AccountApi>(accountApiRef);
  const providersApi = useApi<ProvidersApi>(providersApiRef);
  const toastApi = useApi<ToastApi>(toastApiRef);
  const { closeSettings } = useAccountContext();

  const [displayName, setDisplayName] = useState<string>(
    account.metadata.title ?? '',
  );
  const [description, setDescription] = useState<string>(
    account.metadata.description ?? '',
  );
  const [scanSchedule, setScanSchedule] = useState<Schedule | undefined>(
    account.spec.scanSchedule,
  );
  const [roles, setRoles] = useState<Record<string, AccountUserRole | ''>>(() =>
    Object.fromEntries(
      (account.spec.users ?? []).map(user => [
        normalizeEntityRef(user.UserRef),
        user.Role,
      ]),
    ),
  );
  const [members, setMembers] = useState<UserEntity[]>([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isClosing, setIsClosing] = useState<boolean>(false);
  const [isSettled, setIsSettled] = useState<boolean>(false);

  // The overview behind the page can be long; open at the top.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const ownerGroupRef = useMemo(() => {
    const owner = account.spec.owner ?? '';
    return owner.includes(':') ? owner : `group:default/${owner}`;
  }, [account.spec.owner]);

  useEffect(() => {
    if (!ownerGroupRef) {
      setMembers([]);
      return () => {};
    }

    let ignore = false;

    setIsLoadingMembers(true);
    accountApi
      .getUsers(ownerGroupRef)
      .then(response => {
        if (!ignore) {
          setMembers(
            response.succeeded
              ? ((response.body?.items ?? []) as UserEntity[])
              : [],
          );
        }
      })
      .catch(() => {
        if (!ignore) {
          setMembers([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setIsLoadingMembers(false);
        }
      });

    return () => {
      ignore = true;
    };
  }, [accountApi, ownerGroupRef]);

  const memberRows: MemberRow[] = useMemo(
    () =>
      members.map(member => ({
        key: normalizeEntityRef(member.metadata.name),
        displayName: member.spec.profile?.displayName ?? member.metadata.name,
        userRef: `user:default/${member.metadata.name}`,
      })),
    [members],
  );

  const isDisplayNameInvalid = displayName.trim().length < 3;

  const adminUserKeys = useMemo(
    () =>
      Object.entries(roles)
        .filter(([, role]) => role === AccountUserRole.ADMIN)
        .map(([userKey]) => userKey),
    [roles],
  );

  const isLastAdmin = (userKey: string) =>
    roles[userKey] === AccountUserRole.ADMIN && adminUserKeys.length === 1;

  const getRoleOptions = (userKey: string) => {
    const isDemoteDisabled = isLastAdmin(userKey);

    return [
      { label: 'No role', value: '', disabled: isDemoteDisabled },
      {
        label: 'Viewer',
        value: AccountUserRole.VIEWER,
        disabled: isDemoteDisabled,
      },
      {
        label: 'Contributor',
        value: AccountUserRole.CONTRIBUTOR,
        disabled: isDemoteDisabled,
      },
      { label: 'Admin', value: AccountUserRole.ADMIN },
    ];
  };

  const handleRoleChange = (userKey: string, value: Key | Key[] | null) => {
    const role = (value?.toString() ?? '') as AccountUserRole | '';
    setRoles(previous => ({ ...previous, [userKey]: role }));
  };

  const handleReturn = () => {
    // Play the slide-out animation; closeSettings runs onAnimationEnd.
    setIsClosing(true);
    onUnsettled?.();
  };

  const handleSave = async () => {
    if (isDisplayNameInvalid || isSaving || isClosing) {
      return;
    }

    setIsSaving(true);

    try {
      const users: AccountUser[] = Object.entries(roles)
        .filter(([, role]) => role !== '')
        .map(([userKey, role]) => ({
          UserRef: `user:default/${userKey}`,
          Role: role as AccountUserRole,
        }));

      if (
        (account.spec.users ?? []).some(
          user => user.Role === AccountUserRole.ADMIN,
        ) &&
        !users.some(user => user.Role === AccountUserRole.ADMIN)
      ) {
        toastApi.post({
          title: 'The account must keep at least one Admin.',
          status: 'danger',
          timeout: 10000,
        });
        return;
      }

      const payload: Account = buildAccountPayload(account, {
        Title: displayName.trim(),
        Description: description,
        ScanSchedule: scanSchedule,
        Users: users,
      });

      const response = await accountApi.editAccountEntity(
        account.spec.dynamic_uuid,
        payload,
      );

      if (response.succeeded) {
        await providersApi.triggerProvider('Account');
        toastApi.post({
          title: `Settings of account "${account.metadata.name}" were saved successfully.`,
          status: 'success',
          timeout: 5000,
        });
        refresh?.();
        setIsClosing(true);
        onUnsettled?.();
      } else {
        toastApi.post({
          title: 'An error occurred while saving account settings.',
          description: response.error,
          status: 'danger',
          timeout: 10000,
        });
      }
    } catch (error) {
      toastApi.post({
        title: 'An error occurred while saving account settings.',
        description: (error as Error).message,
        status: 'danger',
        timeout: 10000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const columns: DataColumnConfig[] = [
    {
      id: 'user',
      label: 'User',
      isRowHeader: true,
      cell: row => (
        <CellText
          title={(row.displayName as string) ?? ''}
          description={(row.userRef as string) ?? ''}
        />
      ),
    },
    {
      id: 'role',
      label: 'Role',
      cell: row => (
        <Cell>
          <ModernSelect
            label="Role"
            options={getRoleOptions(row.key as string)}
            value={roles[row.key as string] ?? ''}
            onChange={value => handleRoleChange(row.key as string, value)}
            description={
              isLastAdmin(row.key as string)
                ? 'At least one Admin must remain in the account.'
                : undefined
            }
          />
        </Cell>
      ),
    },
  ];

  // 'fixed' stays in overlay position inside its fixed wrapper; 'overlay'
  // settles in-flow once the slide-in finished covering the tab content.
  const positionClass =
    variant === 'fixed' || !isSettled
      ? styles.pageOverlay
      : styles.pageSettled;

  return (
    <div
      className={[
        styles.page,
        positionClass,
        isClosing ? styles.pageClosing : styles.pageOpening,
      ].join(' ')}
      onAnimationEnd={event => {
        if (event.target !== event.currentTarget) {
          return;
        }
        if (isClosing) {
          closeSettings();
        } else {
          // Slide-in finished: switch from overlay to in-flow layout so the
          // page length matches the settings content, and let the tab hide
          // the overview behind us.
          setIsSettled(true);
          onSettled?.();
        }
      }}
    >
      <Flex direction="column" gap="3" className={styles.content}>
        <Flex justify="between" align="center">
          <Flex gap="2" align="center">
            <ButtonIconBack
              variant="tertiary"
              size="medium"
              onClick={handleReturn}
              isDisabled={isSaving || isClosing}
            />
            <Text variant="title-large" weight="bold">
              Account Settings
            </Text>
          </Flex>
          <Button
            variant="primary"
            onClick={handleSave}
            isDisabled={isSaving || isClosing || isDisplayNameInvalid}
          >
            Save
          </Button>
        </Flex>

        <hr className={styles.divider} />

        <ModernCustomCard title="General">
          <Flex direction="column" gap="3">
            <TextField
              label="Display Name"
              value={displayName}
              onChange={setDisplayName}
              isRequired
              description="Account's name should be at least 3 characters long."
              isInvalid={isDisplayNameInvalid}
            />
            <TextField
              label="Description"
              value={description}
              onChange={setDescription}
            />
          </Flex>
        </ModernCustomCard>

        <hr className={styles.divider} />

        <ModernCustomCard title="Members and Roles">
          <DataTable
            columnConfig={columns}
            data={memberRows}
            loading={isLoadingMembers}
            emptyState={
              <Text variant="body-small">
                No members found in the account owner group.
              </Text>
            }
          />
        </ModernCustomCard>

        {account.spec.authType === AuthType.AWS_INSTANCE_PROFILE && (
          <>
            <hr className={styles.divider} />
            <ModernCustomCard title="Scan Configuration">
              <Flex direction="column" gap="3">
                <AccountScanScheduleInput
                  accountScanSchedule={scanSchedule}
                  setAccountScanSchedule={setScanSchedule}
                />
              </Flex>
            </ModernCustomCard>
          </>
        )}
      </Flex>
    </div>
  );
}
